import { paths } from './path'
import VNWAuthSdk from '@vnetwork/auth'

const authSdk = new VNWAuthSdk(
    import.meta.env.APP_KEYCLOAK_CLIENT_ID,
    import.meta.env.APP_KEYCLOAK_URL,
    import.meta.env.APP_KEYCLOAK_REALM,
    import.meta.env.APP_KEYCLOAK_SERVER_CLIENT_ID,
    import.meta.env.APP_KEYCLOAK_REDIRECT_URI,
    paths.login
)

/**
 * Extended SDK with helper methods
 */
export const authSdkHelpers = {
    /**
     * Ensure authorization access token is available for API calls
     *
     * IMPORTANT: There are 2 types of tokens in the SDK:
     * 1. access_token (localStorage) - Keycloak access token
     * 2. authorization_access_token (RAM only) - UMA ticket token for API authorization
     *
     * When user refreshes page (F5):
     * - access_token persists (localStorage)
     * - authorization_access_token is LOST (RAM only)
     *
     * The /auth/me endpoint requires authorization_access_token, so we must
     * get it on every app load to avoid 401 error.
     *
     * @returns Promise that resolves when authorization token is available
     */
    async ensureValidToken(): Promise<void> {
        const accessToken = authSdk.getAccessToken()
        const authorizationToken = authSdk.authorizationAccessToken()

        // No access token means user is not logged in
        if (!accessToken) {
            return
        }

        // Authorization token already exists in RAM, no need to refresh
        if (authorizationToken) {
            return
        }

        // Authorization token is missing (F5 or first load)
        // Get new UMA ticket token for API authorization
        try {
            const { access_token } = await authSdk.getUtmTicketToken()
            authSdk.setAuthorizationAccessToken(access_token)
        } catch (error) {
            // If access_token is expired, this will trigger 401
            // SDK will auto-refresh and retry
            console.warn(
                '[ensureValidToken] Failed to get authorization token:',
                error
            )
        }
    },
}

export default authSdk
